import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle, XCircle } from 'lucide-react';
import api from '../lib/api';

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ name: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    verifyInvite();
  }, [token]);

  const verifyInvite = async () => {
    try {
      const res = await api.verifyInvite(token);
      setInvite(res.data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Undangan tidak valid atau sudah kedaluwarsa');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.acceptInvite(token, form);
      const { access_token, user } = res.data;
      localStorage.setItem('sellmu_token', access_token);
      setSuccess(true);
      toast.success('Akun berhasil dibuat!');
      setTimeout(() => {
        window.location.href = '/';
      }, 2000);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal membuat akun');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F5F5] flex items-center justify-center">
        <div className="w-10 h-10 border-3 border-[#EB7B44] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F6F5F5] flex items-center justify-center p-4" data-testid="invite-error">
        <Card className="max-w-md w-full bg-white border-[#CACACC] rounded-xl shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <XCircle size={32} className="text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-[#202020] mb-2" style={{ fontFamily: 'Manrope' }}>Undangan Tidak Valid</h2>
            <p className="text-[#64584F] mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} className="bg-[#EB7B44] hover:bg-[#D96A35] text-white">
              Ke Halaman Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#F6F5F5] flex items-center justify-center p-4" data-testid="invite-success">
        <Card className="max-w-md w-full bg-white border-[#CACACC] rounded-xl shadow-sm">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-green-600" />
            </div>
            <h2 className="text-xl font-bold text-[#202020] mb-2" style={{ fontFamily: 'Manrope' }}>Akun Berhasil Dibuat!</h2>
            <p className="text-[#64584F]">Anda akan dialihkan ke dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F6F5F5] flex items-center justify-center p-4" data-testid="invite-page">
      <Card className="max-w-md w-full bg-white border-[#CACACC] rounded-xl shadow-sm">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-2 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#EB7B44] flex items-center justify-center">
              <span className="text-white font-bold text-xl" style={{ fontFamily: 'Manrope' }}>S</span>
            </div>
            <span className="text-2xl font-bold text-[#202020]" style={{ fontFamily: 'Manrope' }}>Sellmu</span>
          </div>
          <CardTitle className="text-xl text-[#202020]" style={{ fontFamily: 'Manrope' }}>
            Terima Undangan
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <div className="bg-[#F6F5F5] rounded-lg p-4 mb-6">
            <p className="text-sm text-[#64584F]">
              <strong className="text-[#202020]">{invite?.admin_name}</strong> mengundang Anda sebagai{' '}
              <strong className="text-[#EB7B44]">{invite?.role?.toUpperCase()}</strong> di toko{' '}
              <strong className="text-[#202020]">{invite?.store_name}</strong>
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label className="text-[#202020] font-medium">Email</Label>
              <Input
                value={invite?.email || ''}
                disabled
                className="h-12 bg-[#F6F5F5] border-[#CACACC] text-[#64584F]"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020] font-medium">Nama Lengkap</Label>
              <Input
                value={form.name}
                onChange={e => setForm({...form, name: e.target.value})}
                placeholder="Masukkan nama lengkap"
                required
                className="h-12 bg-white border-[#CACACC] focus:border-[#EB7B44] focus:ring-1 focus:ring-[#EB7B44] rounded-lg"
                data-testid="invite-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#202020] font-medium">Password</Label>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({...form, password: e.target.value})}
                  placeholder="Min. 6 karakter"
                  required
                  className="h-12 bg-white border-[#CACACC] focus:border-[#EB7B44] focus:ring-1 focus:ring-[#EB7B44] rounded-lg pr-12"
                  data-testid="invite-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A6A2A0] hover:text-[#64584F]"
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-[#EB7B44] hover:bg-[#D96A35] text-white font-semibold rounded-lg text-base transition-all active:scale-[0.98]"
              data-testid="invite-submit-btn"
            >
              {submitting ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : 'Buat Akun & Bergabung'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
